import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, FolderKanban, Users, Calendar,
  CheckCircle, Clock, X, Sparkles
} from "lucide-react";
import AIProjectCreator from "@/components/AIProjectCreator";
import { ProjectsPageHint, EmptyStateHint } from "@/components/OnboardingHints";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAICreator, setShowAICreator] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    deadline: ""
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      } else {
        toast.error("Failed to load projects");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!createForm.title.trim()) {
      toast.error("Project title is required");
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Project created!");
        setShowCreateModal(false);
        setCreateForm({ title: "", description: "", deadline: "" });
        // Navigate to project detail
        navigate(`/enterprise/projects/${data.project.id}`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create project");
      }
    } catch (error) {
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  // Handle AI Project Creator output
  const handleAIApply = (aiContent) => {
    setCreateForm({
      title: aiContent.title || "",
      description: aiContent.description || "",
      deadline: createForm.deadline // Keep existing deadline if set
    });
    setShowAICreator(false);
    setShowCreateModal(true);
    toast.success("AI content applied! Review and customize before creating.");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/enterprise")} data-testid="projects-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FolderKanban className="w-6 h-6 text-blue-600" />
                  Projects
                </h1>
                <p className="text-sm text-gray-500">{projects.length} projects</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 relative">
              <Button 
                onClick={() => setShowAICreator(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                data-testid="create-project-ai-header-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Create with AI
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowCreateModal(true)}
                className="border-blue-300 text-blue-700"
                data-testid="create-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Manual
              </Button>
              {/* First-time hint */}
              <ProjectsPageHint onCreateClick={() => setShowAICreator(true)} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {projects.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FolderKanban className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
              <p className="text-gray-500 mb-4">
                Create your first casting project to organize candidates by role.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button 
                  onClick={() => setShowAICreator(true)} 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="create-project-ai-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Create with AI
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowCreateModal(true)} 
                  className="border-blue-300 text-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Manually
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Card 
                key={project.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/enterprise/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-start justify-between">
                    <span className="truncate">{project.title}</span>
                    {project.selected_count > 0 && (
                      <Badge className="bg-green-100 text-green-800 ml-2 flex-shrink-0">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {project.selected_count}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {project.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <FolderKanban className="w-4 h-4" />
                      {project.role_count || 0} roles
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {project.candidate_count || 0} candidates
                    </span>
                  </div>
                  
                  {project.deadline && (
                    <div className="flex items-center gap-1 text-sm text-orange-600">
                      <Calendar className="w-4 h-4" />
                      Deadline: {formatDate(project.deadline)}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                    <Clock className="w-3 h-3" />
                    Created {formatDate(project.created_at)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Create New Project</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Project Title *</Label>
                <Input
                  placeholder="e.g., Web Series Season 2 Casting"
                  value={createForm.title}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="project-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Textarea
                  placeholder="Brief description of the project..."
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Deadline (optional)</Label>
                <Input
                  type="date"
                  value={createForm.deadline}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, deadline: e.target.value }))}
                  data-testid="project-deadline-input"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateProject} 
                  disabled={creating}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="project-create-submit"
                >
                  {creating ? "Creating..." : "Create Project"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Project Creator Modal */}
      <AIProjectCreator
        isOpen={showAICreator}
        onClose={() => setShowAICreator(false)}
        onApply={handleAIApply}
      />
    </div>
  );
}
